import React, { useEffect, useState, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import styled, { ThemeProvider } from 'styled-components';
import ReactWordcloud from 'react-wordcloud';
import { fetchProjectMetadata, fetchBlogMetadata } from './utils/getMetadata';

// Dark-glass theme matching the header / home page
const theme = {
  colors: {
    primary:           'rgb(var(--gray))',
    primaryDark:       '#6b788b',
    secondary:         'rgb(var(--gray) / 0.7)',
    background:        'rgb(var(--bg))',
    backgroundAlt:     '#05080d',
    sectionBackground: 'rgb(var(--fg) / 0.035)',
    border:            'rgb(var(--fg) / 0.06)',
    borderHi:          'rgb(var(--fg) / 0.14)',
    text:              'rgb(var(--fg) / 0.92)',
    muted:             'rgb(var(--gray) / 0.55)',
  },
  shadows: {
    light:  '0 10px 24px rgb(0 0 0 / calc(0.25 * var(--shadow-mul)))',
    medium: '0 20px 44px rgb(0 0 0 / calc(0.4 * var(--shadow-mul)))',
    strong: '0 32px 64px -16px rgb(0 0 0 / calc(0.55 * var(--shadow-mul)))',
  },
  borderRadius: '20px',
  spacing: {
    small: '1rem',
    medium: '2rem',
    large: '3rem',
  },
};

// Styled components
const AboutContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -4.5rem;
  padding: 10.5rem 1.5rem 5rem;
  background: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 10% 0%, rgba(69, 82, 109, 0.18), transparent 40%),
      radial-gradient(circle at 90% 10%, rgba(107, 120, 139, 0.12), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 9.5rem 1rem 4rem;
  }
`;

const ProfileSection = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  align-items: center;
  gap: 2.2rem;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 1080px;
  padding: 2rem;
  background: ${(props) => props.theme.colors.sectionBackground};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 24px;
  box-shadow: ${(props) => props.theme.shadows.strong};
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
    padding: 1.6rem 1.4rem;
    text-align: center;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 300px;
  aspect-ratio: 840 / 1161;
  border-radius: 24px;
  border: 1px solid ${(props) => props.theme.colors.border};
  object-fit: cover;
  object-position: center top;
  box-shadow: ${(props) => props.theme.shadows.strong};
  justify-self: center;
  background: #0b0f16;
  filter: brightness(0.94) saturate(0.98);

  @media (max-width: 900px) {
    display: none;
  }
`;

const ProfileImageMobile = styled.img`
  display: none;

  @media (max-width: 900px) {
    display: block;
    width: 148px;
    height: 148px;
    border-radius: 50%;
    object-fit: cover;
    object-position: center top;
    border: 1px solid ${(props) => props.theme.colors.borderHi};
    box-shadow: ${(props) => props.theme.shadows.strong};
    justify-self: center;
    align-self: center;
    flex-shrink: 0;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-self: flex-start;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid ${(props) => props.theme.colors.border};
  color: ${(props) => props.theme.colors.primary};
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  @media (max-width: 900px) {
    align-self: center;
  }
`;

const HeroLead = styled.p`
  margin: 0;
  font-size: 1.04rem;
  line-height: 1.85;
  color: ${(props) => props.theme.colors.secondary};
  max-width: 720px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.75;
    text-align: center;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: center;
  margin-top: 0.2rem;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const SecondaryButton = styled.a`
  padding: 0.85rem 1.6rem;
  border-radius: 999px;
  background: rgb(var(--fg) / 0.03);
  color: ${(props) => props.theme.colors.text};
  text-decoration: none;
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid ${(props) => props.theme.colors.border};
  backdrop-filter: blur(8px);
  transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.07);
    border-color: ${(props) => props.theme.colors.borderHi};
    box-shadow: 0 12px 32px -4px rgb(0 0 0 / calc(0.4 * var(--shadow-mul)));
  }
`;

const QuickFactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 1080px;
  margin-bottom: 0.4rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const QuickFactCard = styled.div`
  background: ${(props) => props.theme.colors.sectionBackground};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 16px;
  box-shadow: ${(props) => props.theme.shadows.light};
  padding: 1.2rem 1.2rem 1.05rem;
  backdrop-filter: blur(20px) saturate(1.1);
  -webkit-backdrop-filter: blur(20px) saturate(1.1);
  transition: border-color 0.3s, background 0.3s;

  &:hover {
    border-color: ${(props) => props.theme.colors.borderHi};
    background: rgb(var(--fg) / 0.055);
  }
`;

const QuickFactLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 600;
  color: ${(props) => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const QuickFactText = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.secondary};
  line-height: 1.7;
  font-size: 0.92rem;
`;

const InterestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 0.4rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InterestCard = styled.div`
  background: ${(props) => props.theme.colors.sectionBackground};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 14px;
  padding: 1.1rem 1rem;
  box-shadow: ${(props) => props.theme.shadows.light};
  backdrop-filter: blur(20px);
  transition: border-color 0.3s, background 0.3s;

  &:hover {
    border-color: ${(props) => props.theme.colors.borderHi};
    background: rgb(var(--fg) / 0.055);
  }
`;

const InterestCardTitle = styled.h3`
  margin: 0 0 0.45rem 0;
  font-size: 1rem;
  color: ${(props) => props.theme.colors.text};
  font-weight: 700;
`;

const InterestCardText = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.7;
  color: ${(props) => props.theme.colors.secondary};
`;

const Quote = styled.blockquote`
  font-size: 2.45rem;
  font-weight: 800;
  line-height: 1.12;
  color: ${(props) => props.theme.colors.text};
  margin: 0;
  max-width: 760px;
  letter-spacing: -0.035em;

  span {
    color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: 900px) {
    text-align: center;
    font-size: 1.95rem;
  }

  @media (max-width: 768px) {
    font-size: 1.7rem;
    line-height: 1.18;
  }
`;

const Section = styled.div`
  background: ${(props) => props.theme.colors.sectionBackground};
  box-shadow: ${(props) => props.theme.shadows.medium};
  border-radius: ${(props) => props.theme.borderRadius};
  border: 1px solid ${(props) => props.theme.colors.border};
  padding: 1.7rem 1.9rem;
  margin: 0.85rem 0;
  width: 100%;
  max-width: 1080px;
  text-align: left;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => props.theme.shadows.strong};
    border-color: ${(props) => props.theme.colors.borderHi};
  }

  @media (max-width: 768px) {
    padding: 1.1rem 1.1rem;
    margin: 0.7rem 0;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  margin-bottom: 1rem;
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  letter-spacing: -0.01em;
  line-height: 1.1;
  font-weight: 700;

  &::before {
    content: "";
    width: 3px;
    height: 28px;
    border-radius: 999px;
    background: linear-gradient(180deg, ${(props) => props.theme.colors.primary} 0%, transparent 100%);
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
    gap: 0.6rem;

    &::before {
      width: 3px;
      height: 24px;
    }
  }
`;

const SectionContent = styled.div`
  font-size: 0.98rem;
  line-height: 1.85;
  color: ${(props) => props.theme.colors.secondary};
  margin: 0;

  p {
    margin: 0 0 1rem 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  h3 {
    margin-top: 0.7rem;
    margin-bottom: 0.1rem;
    color: ${(props) => props.theme.colors.primary};
    font-size: 1.08rem;
    text-decoration: none;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  .cv-list {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }
  .cv-list > li {
    margin-bottom: 1.2rem;
    padding-bottom: 1.2rem;
    border-bottom: 1px solid ${(props) => props.theme.colors.border};
  }
  .cv-list > li:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .cv-title {
    font-weight: 700;
    font-size: 1.05rem;
    color: ${(props) => props.theme.colors.text};
    margin-bottom: 0.18rem;
    display: block;
    margin-top: 0.1rem;
    letter-spacing: 0.01em;
  }
  .cv-subtitle {
    font-size: 0.95rem;
    color: rgb(var(--fg) / 0.72);
    margin-bottom: 0.1rem;
    display: block;
  }
  .cv-detail {
    font-size: 0.9rem;
    color: ${(props) => props.theme.colors.muted};
    margin-bottom: 0.3rem;
    display: block;
  }
  .cv-inner-list {
    margin-top: 0.25rem;
    margin-bottom: 0.2rem;
    padding-left: 1.5rem;
    list-style-type: square;
    font-size: 0.92rem;
    color: ${(props) => props.theme.colors.secondary};
  }

  .cv-company {
    font-weight: 700;
    color: ${(props) => props.theme.colors.text};
    font-size: 1.04rem;
    margin-bottom: 0.08rem;
    display: block;
  }
  .cv-role {
    font-weight: 600;
    margin-left: 0.1rem;
    font-size: 0.95rem;
    margin-bottom: 0.04rem;
    display: block;
    color: rgb(var(--fg) / 0.8);
  }
  .cv-exp-details {
    margin-left: 0.1rem;
    margin-bottom: 0.12rem;
    font-size: 0.9rem;
    color: ${(props) => props.theme.colors.muted};
    display: block;
  }
  .cv-exp-list {
    margin-left: 1.2rem;
    margin-top: 0.13rem;
    margin-bottom: 0.13rem;
    padding-left: 1.1rem;
    list-style-type: square;
    font-size: 0.92rem;
    color: ${(props) => props.theme.colors.secondary};
  }

  .cv-interest-title {
    font-weight: 700;
    font-size: 1rem;
    color: ${(props) => props.theme.colors.text};
    margin-bottom: 0.08rem;
    margin-top: 0.05rem;
    display: inline;
  }
  .cv-interest-detail {
    font-size: 0.92rem;
    color: ${(props) => props.theme.colors.secondary};
    margin-left: 0.2rem;
  }

  ul:not(.cv-list):not(.cv-inner-list):not(.cv-exp-list):not(.skill-list) {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    list-style-type: square;
    line-height: 1.6;
  }

  li {
    margin-bottom: 0.32rem;
    margin-top: 0rem;
  }

  strong {
    color: ${(props) => props.theme.colors.text};
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  .subheading {
    font-weight: 700;
    color: ${(props) => props.theme.colors.text};
    margin-bottom: 0.18rem;
    margin-top: 0.45rem;
    font-size: 1.05rem;
    letter-spacing: 0.008em;
    display: block;
  }

  .skill-list {
    margin-left: 2.2rem;
    margin-top: 0.1rem;
    margin-bottom: 0.3rem;
    padding-left: 1.2rem;
    list-style-type: square;
    font-size: 0.94rem;
  }

  a {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.25s, color 0.25s;
  }

  a:hover {
    color: ${(props) => props.theme.colors.text};
    border-bottom-color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 0.92rem;
    line-height: 1.7;
    .cv-list > li {
      padding-bottom: 0.8rem;
      margin-bottom: 0.8rem;
    }
    .cv-title, .cv-company {
      font-size: 0.98rem;
    }
    .cv-detail, .cv-subtitle, .cv-exp-details, .cv-role {
      font-size: 0.86rem;
    }
    .cv-inner-list, .cv-exp-list, .skill-list {
      font-size: 0.86rem;
      padding-left: 0.9rem;
    }
    ul {
      padding-left: 1rem;
    }
  }
`;

const DownloadButton = styled.a`
  padding: 0.85rem 1.6rem;
  background: rgb(var(--fg) / 0.06);
  color: ${(props) => props.theme.colors.text};
  text-decoration: none;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid ${(props) => props.theme.colors.borderHi};
  backdrop-filter: blur(8px);
  transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.12);
    border-color: rgb(var(--fg) / 0.28);
    box-shadow: 0 12px 32px -4px rgb(0 0 0 / calc(0.4 * var(--shadow-mul)));
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.3rem;
    font-size: 0.78rem;
  }
`;


// Two color sets — react-wordcloud passes these to a d3 ordinal scale, so
// they need to be resolved hex/rgb at render time (CSS variables won't
// flow through the canvas pipeline).
const WORDCLOUD_COLORS_DARK = ['#f2f2f2', '#dde1e6', '#9da5ae', '#c5ccd4', '#e8eaed'];
const WORDCLOUD_COLORS_LIGHT = ['#1f2937', '#334155', '#475569', '#64748b', '#94a3b8'];

const buildWordCloudOptions = (theme) => ({
  rotations: 2,
  rotationAngles: [0, -90],
  fontSizes: [16, 52],
  fontFamily: 'Arial, sans-serif',
  fontWeight: 'bold',
  padding: 5,
  colors: theme === 'light' ? WORDCLOUD_COLORS_LIGHT : WORDCLOUD_COLORS_DARK,
  enableTooltip: false,
  deterministic: true,
  spiral: 'archimedean',
});

const WordCloudBox = styled.div`
  width: 100%;
  height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;

  & > div { width: 100%; height: 100%; }

  @media (max-width: 768px) {
    height: 280px;
  }
`;

const SectionLead = styled.p`
  font-size: 0.95rem;
  color: ${(props) => props.theme.colors.muted};
  line-height: 1.6;
  margin: 0 0 1.2rem;
`;

const AboutMe = () => {
  const [tagCloud, setTagCloud] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [projects, blog] = await Promise.all([
        fetchProjectMetadata(),
        fetchBlogMetadata(),
      ]);
      if (cancelled) return;
      const tagCounts = {};
      [...(projects || []), ...(blog || [])].forEach((item) => {
        (item.tags || []).forEach((tag) => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      });
      setTagCloud(
        Object.entries(tagCounts)
          .map(([tag, count]) => ({ text: tag, value: count }))
          .sort((a, b) => b.value - a.value)
      );
    })();
    return () => { cancelled = true; };
  }, []);

  const wordCloudWords = useMemo(() => tagCloud, [tagCloud]);
  const { theme: appTheme } = useTheme();
  const wordCloudOptions = useMemo(() => buildWordCloudOptions(appTheme), [appTheme]);

  return (
    <ThemeProvider theme={theme}>
      <AboutContainer>
        <ProfileSection>
          <ProfileImage src="/images/kp_photo.jpg" alt="Kyriakos Papadopoulos" />
          <ProfileImageMobile src="/images/kp_photo_square.jpg" alt="Kyriakos Papadopoulos" />
          <HeroContent>
            <Quote>
              Building at the <span>intersection of business, data, and technology.</span>
            </Quote>
            <HeroLead>
              Hi, I’m Kyriakos, a BI Analyst with hands-on experience in Power BI, SQL, Excel modeling, and workflow automation. I build reporting systems, operational tools, and structured data processes that reduce manual effort, improve accuracy, and support better decision-making. My work combines business understanding with technical execution, from dashboards and ERP-based analysis to event automation, mobile tooling, and payout models.
            </HeroLead>
            <HeroLead>
              My path has never been purely technical or purely business-oriented. That is exactly what makes it interesting. I work best where analysis, decision-making, communication, and implementation all need to come together.
            </HeroLead>
            <HeroActions>
              <DownloadButton href="/files/cv.pdf" download="kyriakos_papadopoulos_cv.pdf">
                Download My Resume
              </DownloadButton>
              <SecondaryButton href="/projects">
                View Projects
              </SecondaryButton>
            </HeroActions>
          </HeroContent>
        </ProfileSection>

        <QuickFactsGrid>
          <QuickFactCard>
            <QuickFactLabel>Current Focus</QuickFactLabel>
            <QuickFactText>
              Business intelligence, reporting systems, process improvement, and operational automation.
            </QuickFactText>
          </QuickFactCard>
          <QuickFactCard>
            <QuickFactLabel>How I Work</QuickFactLabel>
            <QuickFactText>
              I like structured thinking, practical outputs, and building solutions that reduce friction rather than just looking impressive.
            </QuickFactText>
          </QuickFactCard>
          <QuickFactCard>
            <QuickFactLabel>What Drives Me</QuickFactLabel>
            <QuickFactText>
              Creating clarity from complexity and using technology in ways that actually improve decisions, processes, and day-to-day work.
            </QuickFactText>
          </QuickFactCard>
        </QuickFactsGrid>

        <Section>
          <SectionTitle>My Journey</SectionTitle>
          <SectionContent>
            <p>
              Technology was always the thing that naturally pulled my attention. What was less obvious was how to turn that interest into a direction that felt both realistic and personally meaningful. As I was finishing school, I found myself split between an attraction to computer science and a more practical path through business, management, and the realities of working close to my family’s business environment.
            </p>
            <p>
              I initially chose business because it felt broader and more grounded, but over time I realised that the part of business that genuinely interested me was the part closest to systems, analysis, and improvement. I was drawn less to theory on its own and more to understanding how things work, where they break, and how they can be redesigned to work better.
            </p>
            <p>
              That is what gradually brought me back to technology in a more focused way. First through data analytics, then through a deeper dive into data science, and eventually through practical work involving reporting, SQL, dashboards, automation, and internal tools. What started as a general interest became a clearer direction: building a career around using data and technology to solve real business problems.
            </p>
            <p>
              Today, that combination feels like the right fit. I am not interested in technology for its own sake, and I am not interested in business only at a high level. I am most engaged when I can sit in the middle, understand the business need, work through the data, and help turn insight into something concrete and usable.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Beyond Work</SectionTitle>
          <SectionContent>
            <p>
              Outside work and study, I tend to be drawn to activities that either challenge me physically, expand the way I think, or expose me to different perspectives. Those interests are a big part of how I reset, stay curious, and keep a sense of balance.
            </p>
            <InterestGrid>
              <InterestCard>
                <InterestCardTitle>Fitness</InterestCardTitle>
                <InterestCardText>
                  Weightlifting, running, cycling, and bouldering. I like activities that reward consistency, progression, and discipline over time.
                </InterestCardText>
              </InterestCard>
              <InterestCard>
                <InterestCardTitle>Arts & Media</InterestCardTitle>
                <InterestCardText>
                  Films, music, and non-fiction that offer strong ideas, atmosphere, perspective, or world-building rather than just passive entertainment.
                </InterestCardText>
              </InterestCard>
              <InterestCard>
                <InterestCardTitle>Travel</InterestCardTitle>
                <InterestCardText>
                  Exploring new places, cultures, and ways of living. Travel gives me a sense of perspective and reminds me there is always more than one way to think or work.
                </InterestCardText>
              </InterestCard>
            </InterestGrid>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Themes & Topics</SectionTitle>
          <SectionContent>
            <SectionLead>
              A snapshot of the ideas, tools, and subjects that show up across my projects and writing — sized by how often they recur.
            </SectionLead>
            <WordCloudBox>
              {wordCloudWords.length > 0 && (
                <ReactWordcloud words={wordCloudWords} options={wordCloudOptions} />
              )}
            </WordCloudBox>
          </SectionContent>
        </Section>

      </AboutContainer>
    </ThemeProvider>
  );
};

export default AboutMe;
